import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import InstantSearchAccordion from './units/instantSearchAccordion';
import TabToggle from './tabToggle';

import usePaymentFilter from './custom/usePaymentFilter';
import useQueryParam from '@javascript/lib/hooks/useQueryParam';
import useDebouncedCallback from '@javascript/lib/hooks/useDebouncedCallback';

import { buildPaymentFilterLabel } from '../helpers';

import type { FilterBaseProps } from '../../types/InstantSearch';

import classNames from 'classnames';

type FormData = {
  max?: number;
  min?: number;
};

function useInitialPaymentType() {
  const [paymentTypeParam] = useQueryParam('payment_type');
  return paymentTypeParam === 'cash' ? 'cashPrice' : 'monthlyPayment';
}

const ERROR_MESSAGE = 'Please enter a number';

const formSchema = yup.object().shape({
  min: yup.number().integer(ERROR_MESSAGE).typeError(ERROR_MESSAGE),
  max: yup.number().integer(ERROR_MESSAGE).typeError(ERROR_MESSAGE),
});

const PaymentFilter = ({ filtersOpen }: FilterBaseProps) => {
  const initialPaymentType = useInitialPaymentType();

  const [paymentType, setPaymentType] = useState(initialPaymentType);

  const {
    start: [selectedMin, selectedMax],
    range: { min: lowestPrice, max: highestPrice },
    refine,
  } = usePaymentFilter({
    attribute: paymentType,
  });

  const priceOrPayment =
    paymentType === 'monthlyPayment' ? 'monthly payment' : 'cash price';

  const initialMin = selectedMin || lowestPrice;
  const initialMax = selectedMax || highestPrice;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { min: initialMin, max: initialMax },
    resolver: yupResolver(formSchema),
  });

  // ensure form updates with initial values on first render or tab change
  useEffect(() => {
    setValue('min', initialMin);
    setValue('max', initialMax);
  }, [initialMin, initialMax]);

  const onChange = ({ min, max }: FormData) => refine([min, max]);

  const debouncedOnChange = useDebouncedCallback(handleSubmit(onChange), 300);

  return (
    <InstantSearchAccordion
      filtersOpen={filtersOpen}
      label="Price"
      selectedOptions={buildPaymentFilterLabel({
        highestPrice,
        lowestPrice,
        paymentType,
        selectedMax,
        selectedMin,
      })}
    >
      <TabToggle
        onChange={setPaymentType}
        option1={{ label: 'Monthly payment', value: 'monthlyPayment' }}
        option2={{ label: 'Cash price', value: 'cashPrice' }}
        selectedOption={paymentType}
      />
      <form className="ch-mt--4 ch-mb--6" onChange={debouncedOnChange}>
        <div
          className={classNames('ch-form__group ch-mb--4', {
            'ch-form__group--error': !!errors.min,
          })}
        >
          <label className="ch-fs--3 ch-text--left" htmlFor="min">
            Minimum {priceOrPayment}
          </label>
          {errors.min && (
            <span className="ch-form__control-validation">
              {errors.min?.message}
            </span>
          )}
          <input
            className="ch-form__control ch-bc--grey-6"
            id="min"
            inputMode="numeric"
            type="number"
            {...register('min')}
          />
        </div>
        <div
          className={classNames('ch-form__group', {
            'ch-form__group--error': !!errors.max,
          })}
        >
          <label className="ch-fs--3 ch-mt--1 ch-text--left " htmlFor="max">
            Maximum {priceOrPayment}
          </label>
          {errors.max && (
            <span className="ch-form__control-validation">
              {errors.max?.message}
            </span>
          )}
          <input
            className="ch-form__control ch-bc--grey-6"
            id="max"
            inputMode="numeric"
            type="number"
            {...register('max')}
          />
        </div>
      </form>
    </InstantSearchAccordion>
  );
};

export default PaymentFilter;
